import { type ColumnHelper, createColumnHelper } from '@tanstack/react-table';
import { t } from 'i18next';
import type { ComponentProps } from 'react';
import { Link } from 'react-router';
import { TicketStatusTag } from '#pie/components/ticket-status-tag/TicketStatusTag';
import { getFormattedDate } from '#pie/i18n/dateFormats';
import type { TicketListDto } from '#pie/internal/api/pie-bff';
import { ticketPaginationAtom, ticketSortingAtom } from '#pie/stores/ticket';
import type { SetTypeForFields } from '#pie/utils/date';
import { CopyButton } from '../copy-button/CopyButton';
import { DataTable } from '../data-table/DataTable';

interface TicketTableProps
  extends Omit<
    ComponentProps<typeof DataTable<ParsedTicketListDto>>,
    'columns' | 'sortingAtom'
  > {
  variant?: 'A' | 'B' | 'D';
}

type ParsedTicketListDto = SetTypeForFields<
  TicketListDto,
  [
    'createdOn',
    'eventDate',
    'targetDate',
    'handledByRegionalGridOperatorDate',
    'modifiedOn',
  ],
  Date
>;

export const ticketsColumnParse = ({
  helper = createColumnHelper<ParsedTicketListDto>(),
}: {
  helper?: ColumnHelper<ParsedTicketListDto>;
}) => ({
  createdOn: helper.accessor('createdOn', {
    cell: (info) => {
      const date = info.getValue();
      return date && <span>{getFormattedDate(date)}</span>;
    },
    header: t('overview.table.header.created_on', { ns: 'ticket' }),
  }),
  electricityEan: helper.accessor('electricityEan', {
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <CopyButton content={value} className="relative">
          <Link to={`/berichtenbox/${info.row.original.id}`}>{value}</Link>
        </CopyButton>
      ) : (
        ''
      );
    },
    header: t('common.electricity_ean'),
  }),
  eventDate: helper.accessor('eventDate', {
    cell: (info) => {
      const date = info.getValue();
      return date && <span>{getFormattedDate(date, false)}</span>;
    },
    header: t('common.event_date'),
  }),
  gasEan: helper.accessor('gasEan', {
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <CopyButton content={value} className="relative">
          <Link to={`/berichtenbox/${info.row.original.id}`}>{value}</Link>
        </CopyButton>
      ) : (
        ''
      );
    },
    header: t('common.gas_ean'),
  }),
  handledByRegionalGridOperatorDate: helper.accessor(
    'handledByRegionalGridOperatorDate',
    {
      cell: (info) => {
        const date = info.getValue();
        return date && <span>{getFormattedDate(date)}</span>;
      },
      header: t('overview.table.header.handled_by_rnb', { ns: 'ticket' }),
    },
  ),
  handler: helper.accessor('handler.label', {
    header: t('common.handler'),
  }),
  hasUnreadComments: helper.accessor('hasUnreadComments', {
    cell: (info) => (info.getValue() ? <span>Nee</span> : <span>Ja</span>),
    header: t('overview.table.header.has_unread_comments', { ns: 'ticket' }),
  }),
  issuer: helper.accessor('issuer.label', {
    header: t('common.submitter'),
  }),
  mainCategory: helper.accessor('mainCategory.label', {
    header: t('common.main_category'),
  }),
  modifiedOn: helper.accessor('modifiedOn', {
    cell: (info) => {
      const date = info.getValue();
      return date && <span>{getFormattedDate(date)}</span>;
    },
    header: t('overview.table.header.modified_on', { ns: 'ticket' }),
  }),
  origin: helper.accessor('origin', {
    cell: (origin) => {
      const value = origin.getValue();
      return value ? (
        <span>{t(`overview.common.origin.${value}`, { ns: 'ticket' })}</span>
      ) : (
        <span />
      );
    },
    header: t('overview.table.header.origin', { ns: 'ticket' }),
  }),
  regionalGridOperator: helper.accessor('regionalGridOperator.label', {
    header: t('common.market_roles.RNB'),
  }),
  targetDate: helper.accessor('targetDate', {
    cell: (info) => {
      const targetDate = info.getValue();
      return targetDate && <span>{getFormattedDate(targetDate, false)}</span>;
    },
    header: t('ticket_target-date', { ns: 'ticket' }),
  }),
  status: helper.accessor('status', {
    cell: (info) => {
      const status = info.getValue();
      return <TicketStatusTag status={status} className="w-40" />;
    },
    header: t('common.status'),
  }),
  subCategory: helper.accessor('subCategory.label', {
    header: t('common.sub_category'),
  }),
  ticketNumber: helper.accessor('ticketNumber', {
    cell: (info) => (
      <CopyButton content={info.getValue()} className="relative z-10">
        <Link to={`/berichtenbox/${info.row.original.id}`}>
          {info.getValue()}
        </Link>
      </CopyButton>
    ),
    header: t('ticket_number', { ns: 'ticket' }),
    meta: { columnWidth: 160 },
  }),
  title: helper.accessor('title', {
    header: t('common.subject'),
    meta: { columnWidth: 250 },
  }),
});

export type TicketsColumnName = keyof ReturnType<typeof ticketsColumnParse>;

export function TicketTable({ variant = 'A', ...props }: TicketTableProps) {
  const aColumns: TicketsColumnName[] = [
    'ticketNumber',
    'title',
    'status',
    'targetDate',
    'hasUnreadComments',
    'origin',
    'regionalGridOperator',
    'issuer',
    'handler',
    'createdOn',
    'modifiedOn',
    'eventDate',
    'mainCategory',
    'subCategory',
    'electricityEan',
    'gasEan',
  ];
  const bColumns: TicketsColumnName[] = [
    'ticketNumber',
    'title',
    'status',
    'hasUnreadComments',
    'regionalGridOperator',
    'issuer',
    'handler',
    'mainCategory',
    'createdOn',
    'handledByRegionalGridOperatorDate',
  ];
  const dColumns: TicketsColumnName[] = [
    'ticketNumber',
    'title',
    'regionalGridOperator',
    'status',
    'modifiedOn',
  ];

  return (
    <DataTable
      sortingAtom={ticketSortingAtom}
      paginationAtom={ticketPaginationAtom}
      columns={(helper) =>
        ({ A: aColumns, B: bColumns, D: dColumns })[variant].map(
          (columnName) => ticketsColumnParse({ helper: helper })[columnName],
        )
      }
      pageSizes={[10, 25, 50]}
      {...props}
    />
  );
}
